import React, { useState } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Link as ChakraLink,
  Icon,
  Spacer,
  Button,
  Text,
  Avatar,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiBox, FiBook, FiLogOut, FiKey, FiShield, FiClipboard, FiServer, FiTrendingUp, FiHeart } from 'react-icons/fi';

import Applications from './components/Applications';
import ApiKeys from './components/ApiKeys';
import SuperAdmin from './components/SuperAdmin';
import AuditLog from './components/AuditLog';
import ApiDocs from './components/ApiDocs';
import PostgresStatus from './components/PostgresStatus';
import UsageAnalytics from './components/UsageAnalytics';
import HealthCheck from './components/HealthCheck';
import LoginForm from './components/Login';

const NavButton = ({ to, icon, children, isActive }) => (
  <ChakraLink as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      width="full"
      justifyContent="flex-start"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'blue' : 'whiteAlpha'}
      color={isActive ? 'white' : 'whiteAlpha.800'}
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      _hover={{
        bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight={isActive ? '600' : '400'}
    >
      {children}
    </Button>
  </ChakraLink>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = () => {
    setToken(localStorage.getItem('token'));
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <Flex height="100vh" bg="gray.50">
      {/* Sidebar */}
      <VStack
        as="nav"
        width="280px"
        py={6}
        px={4}
        bgGradient="linear(to-b, cyan.600, cyan.800)"
        spacing={2}
        align="stretch"
        boxShadow="xl"
        overflowY="auto"
        flexShrink={0}
      >
        {/* Logo/Brand */}
        <Flex align="center" px={4} mb={6}>
          <Box
            w={12}
            h={12}
            borderRadius="full"
            overflow="hidden"
            bg="white"
            p={0.5}
          >
            <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          </Box>
          <Box ml={3}>
            <Text fontSize="lg" fontWeight="bold" color="white">Registry</Text>
          </Box>
        </Flex>

        <Divider borderColor="whiteAlpha.300" mb={4} />

        {/* Navigation */}
        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
          Management
        </Text>
        <NavButton to="/" icon={FiBox} isActive={location.pathname === '/'}>
          Applications
        </NavButton>
        <NavButton to="/api-keys" icon={FiKey} isActive={location.pathname === '/api-keys'}>
          API Keys
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Administration
        </Text>
        <NavButton to="/super-admin" icon={FiShield} isActive={location.pathname === '/super-admin'}>
          Super Admin
        </NavButton>
        <NavButton to="/audit-log" icon={FiClipboard} isActive={location.pathname === '/audit-log'}>
          Audit Log
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Insights
        </Text>
        <NavButton to="/analytics" icon={FiTrendingUp} isActive={location.pathname === '/analytics'}>
          Usage Analytics
        </NavButton>
        <NavButton to="/health" icon={FiHeart} isActive={location.pathname === '/health'}>
          Health Check
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Infrastructure
        </Text>
        <NavButton to="/postgres" icon={FiServer} isActive={location.pathname === '/postgres'}>
          PostgreSQL Status
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Developer
        </Text>
        <NavButton to="/api" icon={FiBook} isActive={location.pathname === '/api'}>
          API Documentation
        </NavButton>

        <Spacer />

        {/* User Section */}
        <Divider borderColor="whiteAlpha.300" />
        <Flex align="center" px={4} py={3}>
          <Avatar size="sm" bg="blue.400" />
          <Box ml={3} flex={1}>
            <Text fontSize="sm" color="white" fontWeight="medium">Admin User</Text>
            <Text fontSize="xs" color="whiteAlpha.600">Logged in</Text>
          </Box>
        </Flex>
        <Button
          leftIcon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          width="full"
          justifyContent="flex-start"
          variant="ghost"
          color="whiteAlpha.800"
          _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
          borderRadius="lg"
        >
          Logout
        </Button>
      </VStack>

      {/* Main Content */}
      <Box flex="1" p={8} overflowY="auto">
        <Routes>
          <Route path="/" element={<Applications />} />
          <Route path="/api-keys" element={<ApiKeys />} />
          <Route path="/super-admin" element={<SuperAdmin />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/postgres" element={<PostgresStatus />} />
          <Route path="/analytics" element={<UsageAnalytics />} />
          <Route path="/health" element={<HealthCheck />} />
          <Route path="/api" element={<ApiDocs />} />
        </Routes>
      </Box>
    </Flex>
  );
}

export default App;